/**
 * apps/web/src/lib/sseClient.ts
 *
 * Parses a fetch Response body (a byte stream of Server-Sent Events) into typed
 * AiProgressEvent objects. We key off the JSON payload's `type` discriminant
 * rather than the SSE `event:` line, so a single parser handles every frame.
 *
 * fetch + ReadableStream is used instead of EventSource because EventSource
 * cannot issue a POST or send credentials/headers the way our auth needs.
 */

import type { AiProgressEvent } from '@glarno/contracts/types';

/** Parse one raw SSE frame (its `data:` lines) into an event, or null if non-JSON. */
export function parseSseFrame(raw: string): AiProgressEvent | null {
  const dataLines = raw
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).replace(/^ /, ''));
  if (dataLines.length === 0) return null;
  try {
    return JSON.parse(dataLines.join('\n')) as AiProgressEvent;
  } catch {
    return null;
  }
}

/** Yield typed events from an SSE byte stream until it closes or `signal` aborts. */
export async function* parseSseStream(
  stream: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<AiProgressEvent> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const event = parseSseFrame(frame);
        if (event) yield event;
        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
}
